import React, { useState, useEffect, useContext } from 'react'
import { Redirect, useParams, useHistory } from "react-router-dom";
import { withTransaction } from '@elastic/apm-rum-react'

import {
	Modal, ModalHeader, ModalBody, ModalFooter, Button
} from 'reactstrap'

import AddJobButton from '../partials/AddJobButton'

import { Form } from '../../components'
import { JobList } from '../../components/job/JobList'
import { JobAppContext } from '../../GlobalState'
import { useJobList, useAddJob, useSearchJobs } from '../../hooks'

const tabs = {
	OPEN: 'open',
	REVIEW: 'pending',
	SUBMITTED: 'submitted',
	ARCHIVED: 'archived',
	SEARCH: 'search',
}

const JobsPage = (props) => {

	const params = useParams();
	const history = useHistory();

	const { user } = useContext(JobAppContext);
	
	const [ jobCreated, setJobCreated ] = useState(false);

	const [ activeTab, setActiveTab ] 		= useState(params?.list ? tabs[params?.list.toUpperCase()] : tabs.OPEN);
	
	const [ searchCount, setSearchCount ] 	= useState(null);
	const [ searchTerm, setSearchTerm ] 	= useState('');

	const [ list_count, setListCount ] 		= useState(user?.list_count ?? 100);
	const [ column_choice, setColumnChoice ] = useState(user?.column_choice ?? 'form_variant');
	
	const [ currentPage, setCurrentPage ] 	= useState(1);
	const [ sortBy, setSortBy ] 			= useState('created_at');
	const [ sortDirection, setSortDirection ] = useState('desc');

	const [ linkModal, setLinkModal ] 		= useState(false);
	const [ previewLabel, setPreviewLabel ] = useState('');
	const [ previewLink, setPreviewLink ] 	= useState('');
	
	const [ copied, setCopied ] 			= useState(false);

	// React Query hooks - only fetch data for the active tab
	const getStatusForTab = (tab) => {
		switch(tab) {
			case tabs.OPEN: return 'open';
			case tabs.REVIEW: return 'pending';
			case tabs.SUBMITTED: return 'submitted';
			case tabs.ARCHIVED: return 'archived';
			default: return null;
		}
	};
	
	const currentStatus = getStatusForTab(activeTab);
	const {
		data,
		isLoading: isLoadingJobs,
		error: jobsError
	} = useJobList(currentStatus, currentPage, list_count, sortBy, sortDirection);
	
	const currentJobs = data?.jobs || [];
	const pagination = data?.pagination || {};
	
	const addJobMutation = useAddJob();
	const searchJobsMutation = useSearchJobs();

	// Map current jobs to the appropriate variable name for rendering
	const openJobs = activeTab === tabs.OPEN ? currentJobs : [];
	const pendingJobs = activeTab === tabs.REVIEW ? currentJobs : [];
	const submittedJobs = activeTab === tabs.SUBMITTED ? currentJobs : [];
	const archivedJobs = activeTab === tabs.ARCHIVED ? currentJobs : [];

	// Determine current loading state based on active tab
	const isLoading = (activeTab === tabs.SEARCH) ? searchJobsMutation.isLoading : isLoadingJobs;

	// Get the error for the current tab
	const error = jobsError;

	// anytime you change tabs, update the active tab state and reset to page 1
	useEffect(() => {
		if ( params?.list ) {
			var urlParm = tabs[params.list.toUpperCase()];
			setActiveTab(urlParm);
			// Reset to page 1 and default sort when changing tabs
			setCurrentPage(1);
			switch(urlParm) {
				case tabs.OPEN:
					setSortBy('created_at');
					setSortDirection('desc');
					break;
				case tabs.REVIEW:
					setSortBy('review_at');
					setSortDirection('desc');
					break;
				case tabs.SUBMITTED:
					setSortBy('imported_at');
					setSortDirection('desc');
					break;
				case tabs.ARCHIVED:
					setSortBy('updated_at');
					setSortDirection('desc');
					break;
			}
		} else if (activeTab !== tabs.SEARCH) {
			// If no list in URL and not in search mode, default to open
			setActiveTab("open");
			setCurrentPage(1);
			setSortBy('name');
			setSortDirection('asc');
		}
		// Don't reset if we're in SEARCH mode (activeTab === tabs.SEARCH) to preserve search state
	}, [params?.list])
	
	// Handle column sorting
	const handleSort = (column) => {
		if (sortBy === column) {
			// Toggle direction if clicking same column
			setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
		} else {
			// New column, default to ascending
			setSortBy(column);
			setSortDirection('asc');
		}
		// Reset to page 1 when sorting changes
		setCurrentPage(1);
	}

	const handleSubmit = (formdata) => {
		addJobMutation.mutate({ ...formdata }, {
			onSuccess: (data) => {
				if ( data?.history_record?.previewLink != '' ) {
					setPreviewLabel(data.history_record.previewLabel)
					setPreviewLink(data.history_record.previewLink)
					setLinkModal(true);
				} else {
					setJobCreated(data.id); // setting this value will redirect user to that job
				}
			},
			onError: (error) => {
				console.error('Error creating job:', error);
			}
		});
	}
	
	/* ************************************************************************ */
	/*  Search Functions                                                        */
	/* ************************************************************************ */
	const clearSearch = () => {
		// Clear all search-related state
		setSearchTerm('');
		setSearchCount(null);
		
		// Reset the search mutation to clear results
		searchJobsMutation.reset();
		
		// Navigate back to the open jobs list
		history.push('/jobs/open');
		setActiveTab(tabs.OPEN);
	}

	const searchChange = event => {
		const { target } = event
		const { name, value, checked } = target
		setSearchTerm(value);
	}

	const submitSearch = () => {
		let search_term = searchTerm.toLowerCase()
		if ( search_term != '' ) {
			// Update URL to remove list selection when searching
			history.push('/jobs');
			setActiveTab(tabs.SEARCH);
			searchJobsMutation.mutate(search_term, {
				onSuccess: (data) => {
					setSearchCount(data.resultCount);
				}
			});
		}
	}
	/* ************************************************************************ */

	const toggleLinkModal = () => {
		setLinkModal(!linkModal);
	}

	const copyLink = (shareLink) => {
		let textArea = document.createElement('textarea');
		textArea.setAttribute('style','width:1px;border:0;opacity:0;');
		document.body.appendChild(textArea);
		textArea.value = shareLink;
		textArea.select();
		textArea.focus();
		document.execCommand('copy');
		document.body.removeChild(textArea);
		setCopied(true);
	}

	const closeLinkModal = () => {
		setCopied(false);
		setLinkModal(false);
	}

	const changeTab = (tab) => {
		console.log('switch to tab: ', tab)
		setActiveTab(tab);
	}
	
	const tab_label = (list) => {
		var label = "Open";
		switch (list) {
			case "open": label = "Open"; break;
			case "pending": label = "Review"; break;
			case "submitted": label = "Submitted"; break;
			case "archived": label = "Archived"; break;
			case "search": label = "Search"; break;
		}
		return label;
	}
	
	// Reusable pagination controls component
	const renderPaginationControls = () => {
		if (activeTab === tabs.SEARCH || !pagination.total || pagination.total === 0) {
			return null;
		}
		
		return (
			<div className='d-flex align-items-center'>
				<span className='text-muted mr-3' style={{ fontSize: '14px' }}>
					Page {pagination.current_page || 1} of {pagination.last_page || 1} 
					<span className='ml-2'>({pagination.total || 0} total)</span>
				</span>
				<div className='btn-group'>
					<button 
						className='btn btn-sm btn-outline-secondary'
						onClick={() => setCurrentPage(1)}
						disabled={currentPage === 1 || isLoadingJobs}
					>
						<i className="fa fa-angle-double-left"></i>
					</button>
					<button 
						className='btn btn-sm btn-outline-secondary'
						onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
						disabled={currentPage === 1 || isLoadingJobs}
					>
						<i className="fa fa-angle-left"></i> Prev
					</button>
					<button 
						className='btn btn-sm btn-outline-secondary'
						onClick={() => setCurrentPage(prev => prev + 1)}
						disabled={!pagination.has_more || isLoadingJobs}
					>
						Next <i className="fa fa-angle-right"></i>
					</button>
					<button 
						className='btn btn-sm btn-outline-secondary'
						onClick={() => setCurrentPage(pagination.last_page)}
						disabled={currentPage === pagination.last_page || isLoadingJobs}
					>
						<i className="fa fa-angle-double-right"></i>
					</button>
				</div>
			</div>
		);
	}

	const company = user?.memberships[0].company;
	const reference_label = company?.reference_label != '' ? company.reference_label : 'Customer Reference #';

	return (
		<>
			{jobCreated && <Redirect to={`/job/${jobCreated}`} />}
			{!user && <Redirect to="/login" />}

			<Modal isOpen={linkModal} toggle={closeLinkModal} style={{ 'maxWidth': '650px' }}>
				<ModalHeader toggle={closeLinkModal}>Job Created!</ModalHeader>
				<ModalBody>
					<div>
						<label>{previewLabel}</label>
						<div style={{fontSize: '14px'}}>{previewLink}</div>
					</div>
				</ModalBody>
				<ModalFooter>
					{copied && (
						<i className="far fa-check-circle" 
							style={{ color: '#007bff', fontSize: '24px', fontWeight: 'bold', marginLeft: '10px', verticalAlign: 'middle' }} 
							title="Copied!"></i>
					)}
					<Button color="primary" className="Button float-left" onClick={() => copyLink(previewLink)}>
						<i className="far fa-copy font-weight-bold mr-2"></i> 
						Copy
					</Button>
					<Button color="secondary" className="Button float-right" onClick={closeLinkModal}>
						Close Window
					</Button>
				</ModalFooter>
			</Modal>

			<div className="page-navbar">
				<div className="d-flex justify-content-between align-items-center py-3 px-4">
					<Form onSubmit={submitSearch} className="mr-5">
						<div className="form-row">
							<div id="search_container" className='overflow-hidden rounded-pill'>
								<div className="input-group align-items-center">
									<span className="input-group-text bg-transparent border-0">
										<i className="fas fa-search text-muted ml-2" />
									</span>
									<input type="text" name="searchField" className="form-control shadow-none" id="searchField" value={searchTerm} onChange={searchChange} 
											placeholder={"Search"} disabled={isLoading} />
									<div className="input-group-text bg-transparent border-0 mx-2" style={{ width: '27px', visibility: searchTerm != '' ? 'inherit' : 'hidden' }}>
										<span role="button" onClick={clearSearch}>
											<i className="fa fa-times text-muted"></i>
										</span>
									</div>
									<button className="btn btn-light border shadow-sm text-muted rounded-pill m-1 py-1" type="submit" disabled={isLoading ? 'disabled' : ''} style={{fontSize: '14px'}}>
										Search all jobs
										<i className="fa fa-arrow-right ml-2 fa-xs"></i>
									</button>
								</div>
							</div>
						</div>
					</Form>
					<div>
						<AddJobButton submit={handleSubmit} isSubmitting={addJobMutation.isLoading} />
					</div>
				</div>
			</div>
		
			<div className="padded-content px-0">
				{/* Breadcrumb and Top Pagination */}
				<div className='d-flex align-items-center justify-content-between pl-4 pr-4 py-3 w-100 bg-white border-bottom'>
					<div className='d-flex align-items-center'>
						<span className='h5 mb-0'>
							Jobs
						</span>
						<i className="fa fa-chevron-right mx-2 text-muted fa-xs"></i>
						<span className='h5 mb-0 text-primary'>
							{tab_label(activeTab)}
						</span>
					</div>
					
					{/* Pagination Controls - Top */}
					{renderPaginationControls()}
				</div>
				
			{/* Job List Content */}
			<div className='w-100'>

			{/* Loading indicator for initial load */}
			{isLoadingJobs && currentJobs.length === 0 && activeTab !== tabs.SEARCH ? (
				<div className="d-flex justify-content-center align-items-center p-5">
					<div className="text-center">
						<i className="fas fa-sync-alt fa-spin fa-3x text-primary mb-3"></i>
						<p className="text-muted">Loading jobs...</p>
					</div>
				</div>
			) : (
				<>
					{activeTab === tabs.OPEN && (
						<JobList 
							status="open" 
							jobs={openJobs} 
							isFetching={isLoadingJobs} 
							error={error} 
							onSort={handleSort}
							sortBy={sortBy}
							sortDirection={sortDirection}
							options={{
								'list_count': list_count,
								'column_choice': column_choice,
								'reference_label': reference_label
							}} 
						/>
					)}

					{activeTab === tabs.REVIEW && (
						<JobList 
							status="pending" 
							jobs={pendingJobs} 
							isFetching={isLoadingJobs} 
							error={error}
							onSort={handleSort}
							sortBy={sortBy}
							sortDirection={sortDirection}
							options={{
								'list_count': list_count,
								'column_choice': column_choice,
								'reference_label': reference_label
							}} 
						/>
					)}

					{activeTab === tabs.SUBMITTED && (
						<>
							{company.archive_length >= 0 && (
								<cite className="d-block mx-2 my-3">
									Note: Jobs archived after: {company.archive_length + " " + company.archive_duration + "(s)"}
								</cite>
							)}
							<JobList 
								status={"submitted"} 
								jobs={submittedJobs} 
								isFetching={isLoadingJobs} 
								error={error}
								onSort={handleSort}
								sortBy={sortBy}
								sortDirection={sortDirection}
								options={{
									'list_count': list_count,
									'column_choice': column_choice,
									'reference_label': reference_label
								}} 
							/>
						</>
					)}

					{activeTab === tabs.ARCHIVED && (
						<JobList 
							status={"archived"} 
							jobs={archivedJobs} 
							isFetching={isLoadingJobs} 
							error={error}
							onSort={handleSort}
							sortBy={sortBy}
							sortDirection={sortDirection}
							options={{
								'list_count': list_count,
								'column_choice': column_choice,
								'reference_label': reference_label
							}} 
						/>
					)}
				</>
			)}

		{activeTab === tabs.SEARCH && (
			<>
				{searchJobsMutation.isLoading ? (
					<div className="d-flex justify-content-center align-items-center p-5">
						<div className="text-center">
							<i className="fas fa-sync-alt fa-spin fa-3x text-primary mb-3"></i>
							<p className="text-muted">Searching...</p>
						</div>
					</div>
				) : !isNaN(searchCount) && searchCount > 0 ? (
					<>
						<div className="pl-3 my-3" style={{ color: '#007bff' }}>
							<a onClick={clearSearch} style={{ cursor: 'pointer' }}>
								<i className="fas fa-times-circle"></i>
								<span className="ml-2">Clear Search Results ({searchCount} found)</span>
							</a>
						</div>
						<JobList 
							status="search" 
							jobs={searchJobsMutation.data?.found_jobs || []} 
							isFetching={searchJobsMutation.isLoading} 
							error={error} 
							options={{
								'list_count': list_count,
								'column_choice': column_choice,
								'reference_label': reference_label
							}} 
						/>
					</>
				) : searchCount === 0 ? (
					<div className="m-4 p-4 border rounded bg-light text-center">
						<i className="fas fa-search-minus fa-2x text-muted mb-3"></i>
						<p className="mb-2 font-weight-bold">No results found</p>
						<p className="mb-0 text-muted">Try a different search term or <a onClick={clearSearch} style={{ cursor: 'pointer', color: '#007bff' }}>clear your search</a>.</p>
					</div>
				) : (
					<div className="m-4 p-4 border rounded bg-light text-center">
						<i className="fas fa-search fa-2x text-muted mb-3"></i>
						<p className="mb-0">Enter a search term above to search across all job lists.</p>
					</div>
				)}
			</>
		)}

			</div>
				
			{/* Pagination Controls - Bottom */}
			{!isLoading && currentJobs.length > 0 && (
				<div className='d-flex align-items-center justify-content-end pl-4 pr-4 py-3 w-100 bg-white border-top'>
					{renderPaginationControls()}
				</div>
			)}
			</div>
		</>
	)
}
export default withTransaction('JobsPage', 'component')(JobsPage);